import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, type CreateSafeTransactionInput } from '@gc/shared';

import { ApiError } from '@/lib/api';
import { iqdAmountOf, recordSafeTransaction } from './useSafe';

interface RecordSafeTransactionDialogProps {
  kind: 'OWNER_WITHDRAWAL' | 'OWNER_DEPOSIT';
  /** Current cached balance, for the resulting-balance preview. */
  balanceMd: string;
  currencySymbol: string;
  onClose: () => void;
  onRecorded: () => void;
}

/**
 * The exact wire payload — exported for tests. The amount is ALWAYS positive
 * on the wire; the server signs it by kind, so a client bug can never turn a
 * withdrawal into income by flipping a sign.
 */
export function buildSafeTransactionPayload(
  kind: RecordSafeTransactionDialogProps['kind'],
  amountIqd: number,
  note: string,
): CreateSafeTransactionInput {
  return { kind, amountIqd, note: note.trim() };
}

/** Resulting cached balance after the movement — BigInt, display only. */
export function resultingBalanceMd(
  balanceMd: string,
  kind: RecordSafeTransactionDialogProps['kind'],
  amountIqd: number,
): string {
  const amountMd = BigInt(amountIqd) * 1000n;
  const current = BigInt(balanceMd);
  return (kind === 'OWNER_WITHDRAWAL' ? current - amountMd : current + amountMd).toString();
}

/**
 * Record an owner withdrawal / deposit (SPEC §12.1) — the centered-modal
 * pattern of `StockAdjustDialog`. The note is required by the shared schema:
 * a movement of the venue's money with no stated reason is exactly what the
 * voucher ledger exists to prevent.
 */
export function RecordSafeTransactionDialog({
  kind,
  balanceMd,
  currencySymbol,
  onClose,
  onRecorded,
}: RecordSafeTransactionDialogProps) {
  const { t } = useTranslation();

  const [amountRaw, setAmountRaw] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const amountIqd = iqdAmountOf(amountRaw);
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  const isWithdrawal = kind === 'OWNER_WITHDRAWAL';
  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (amountIqd == null || !note.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      await recordSafeTransaction(buildSafeTransactionPayload(kind, amountIqd, note));
      // The parent closes the dialog and re-fetches balance + ledger; the new
      // voucher appearing at the top of the table IS the confirmation.
      onRecorded();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('safe.record.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
        aria-modal="true"
        aria-label={t(isWithdrawal ? 'safe.record.withdrawTitle' : 'safe.record.depositTitle')}
        className="w-full max-w-md rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">
          {t(isWithdrawal ? 'safe.record.withdrawTitle' : 'safe.record.depositTitle')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-content-secondary">
          {t(isWithdrawal ? 'safe.record.withdrawIntro' : 'safe.record.depositIntro')}
        </p>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('safe.record.amount')}
          </span>
          <div className="flex items-center gap-2">
            <input
              value={amountRaw}
              onChange={(event) => setAmountRaw(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              autoComplete="off"
              className={`${inputClass} tabular`}
            />
            <span className="text-sm text-content-muted">{currencySymbol}</span>
          </div>
          <span className="mt-1 block text-xs text-content-muted">
            {t('safe.record.amountHint')}
          </span>
        </label>

        {amountIqd != null ? (
          <p className="tabular mt-2 text-sm text-content-secondary">
            {t('safe.record.resulting', {
              amount: money(resultingBalanceMd(balanceMd, kind, amountIqd)),
            })}
          </p>
        ) : null}

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">{t('safe.record.note')}</span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={500}
            required
            placeholder={t('safe.record.notePlaceholder')}
            className={inputClass}
          />
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('safe.record.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || amountIqd == null || !note.trim()}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('safe.record.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
